import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { documentsApi, businessesApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { X, RefreshCcw, Zap, Loader2, RotateCw, RotateCcw, Crop, Check, Plus, FileText, Building2, ChevronDown } from "lucide-react";

const FILTERS = [
  { key: "none", label: "None", filter: "none" },
  { key: "bw", label: "B&W", filter: "grayscale(100%) contrast(1.1)" },
  { key: "hi-contrast", label: "Contrast", filter: "contrast(1.35) brightness(1.05)" },
  { key: "sharpen", label: "Sharp", filter: "contrast(1.2) brightness(1.1) saturate(1.15)" },
  { key: "receipt", label: "Receipt", filter: "grayscale(100%) contrast(1.5) brightness(1.1)" },
];

// Convert a page (blob + rotation/filter/crop) into a rendered JPEG blob.
// Used both to preview the merged PDF and to build the final upload.
async function renderPage(page) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (page.crop) {
        sx = (page.crop.x1 / 100) * img.width;
        sy = (page.crop.y1 / 100) * img.height;
        sw = ((page.crop.x2 - page.crop.x1) / 100) * img.width;
        sh = ((page.crop.y2 - page.crop.y1) / 100) * img.height;
      }
      const rot = ((page.rotation % 360) + 360) % 360;
      if (rot === 90 || rot === 270) { c.width = sh; c.height = sw; } else { c.width = sw; c.height = sh; }
      const ctx = c.getContext("2d");
      ctx.filter = FILTERS.find((f) => f.key === page.filter)?.filter || "none";
      ctx.translate(c.width / 2, c.height / 2);
      ctx.rotate((rot * Math.PI) / 180);
      ctx.drawImage(img, sx, sy, sw, sh, -sw / 2, -sh / 2, sw, sh);
      c.toBlob((blob) => resolve({ blob, dataUrl: c.toDataURL("image/jpeg", 0.92), width: c.width, height: c.height }), "image/jpeg", 0.92);
    };
    img.src = page.url;
  });
}

// Find the document (largest bright region) in a small grayscale frame.
// Returns a box in 0..1 video coordinates, or null.
function detectDocument(gray, w, h) {
  const hist = new Array(256).fill(0);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  let sum = 0; for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, best = 0, thr = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t]; if (!wB) continue;
    const wF = gray.length - wB; if (!wF) break;
    sumB += t * hist[t];
    const mB = sumB / wB, mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > best) { best = between; thr = t; }
  }
  const seen = new Uint8Array(w * h);
  const stack = new Int32Array(w * h);
  let top = null;
  for (let s = 0; s < w * h; s++) {
    if (seen[s] || gray[s] <= thr) continue;
    let sp = 0, n = 0, x1 = w, y1 = h, x2 = 0, y2 = 0;
    stack[sp++] = s; seen[s] = 1;
    while (sp) {
      const p = stack[--sp]; n++;
      const x = p % w, y = (p / w) | 0;
      if (x < x1) x1 = x; if (x > x2) x2 = x; if (y < y1) y1 = y; if (y > y2) y2 = y;
      const nb = [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, y > 0 ? p - w : -1, y < h - 1 ? p + w : -1];
      for (const q of nb) if (q >= 0 && !seen[q] && gray[q] > thr) { seen[q] = 1; stack[sp++] = q; }
    }
    if (!top || n > top.n) top = { n, x1, y1, x2, y2 };
  }
  if (!top) return null;
  const bw = top.x2 - top.x1 + 1, bh = top.y2 - top.y1 + 1;
  const areaFrac = (bw * bh) / (w * h);
  if (areaFrac < 0.06 || areaFrac > 0.97 || top.n / (bw * bh) < 0.45) return null;
  return { x1: top.x1 / w, y1: top.y1 / h, x2: (top.x2 + 1) / w, y2: (top.y2 + 1) / h };
}

export default function ScanCamera() {
  const { user } = useAuth();
  const nav = useNavigate();
  const videoRef = useRef();
  const canvasRef = useRef();
  const [stream, setStream] = useState(null);
  // pages: array of {id, blob, url, rotation, filter, crop}
  const [pages, setPages] = useState([]);
  // idx of the page being edited; -1 when in camera mode capturing new page
  const [editIdx, setEditIdx] = useState(-1);
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [flashOn, setFlashOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [cropping, setCropping] = useState(false);
  const [detected, setDetected] = useState(null); // {box (0..1 video), view: {left,top,width,height} px}
  const detectedRef = useRef(null);
  const viewRef = useRef();
  const smallRef = useRef();

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      // Non-master users only see businesses they're assigned to (server-side
      // filter). Default to the first one; switcher is enabled whenever they
      // have more than one so accounting users can scan for the right entity.
      if (!isMaster(user)) setBizId(userBusinessIds(user)[0] || b[0]?.id || "");
      else if (b.length) setBizId(b[0].id);
    });
    startCam();
    return () => stopCam();
  }, []);

  const startCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
      const track = s.getVideoTracks()[0];
      const caps = track.getCapabilities ? track.getCapabilities() : {};
      setTorchSupported(!!caps.torch);
    } catch (e) {
      setError("Camera access denied or unavailable. Use the upload page instead.");
    }
  };

  // The <video> element unmounts when the user enters page-edit mode (a
  // captured thumbnail is being reviewed). When they hit "Add another page"
  // and return to camera mode, React mounts a FRESH <video> — but its
  // `srcObject` is empty until we re-attach the still-live MediaStream.
  // Without this, the viewfinder stays black for branch users (iter70 bug).
  useEffect(() => {
    if (editIdx === -1 && videoRef.current && stream) {
      // Only re-assign if the element doesn't already carry the stream —
      // avoids interrupting a playing video for no reason.
      if (videoRef.current.srcObject !== stream) {
        videoRef.current.srcObject = stream;
        // Some mobile browsers (Safari especially) need an explicit play()
        // call after re-attaching — autoPlay alone doesn't always fire.
        videoRef.current.play().catch(() => {});
      }
    }
  }, [editIdx, stream]);

  // Live document detection (~4 fps) while the camera is showing.
  useEffect(() => {
    if (editIdx !== -1 || !stream) return undefined;
    const id = setInterval(() => {
      const v = videoRef.current, box = viewRef.current;
      if (!v || !box || !v.videoWidth) return;
      const w = 160, h = Math.round((160 * v.videoHeight) / v.videoWidth);
      if (!smallRef.current) smallRef.current = document.createElement("canvas");
      const c = smallRef.current; c.width = w; c.height = h;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, 0, 0, w, h);
      const d = ctx.getImageData(0, 0, w, h).data;
      const gray = new Uint8Array(w * h);
      for (let i = 0; i < w * h; i++) gray[i] = (d[i * 4] * 3 + d[i * 4 + 1] * 6 + d[i * 4 + 2]) / 10;
      const found = detectDocument(gray, w, h);
      const prev = detectedRef.current?.box;
      const next = found && prev
        ? Object.fromEntries(Object.keys(found).map((k) => [k, prev[k] * 0.5 + found[k] * 0.5]))
        : found;
      if (!next) { detectedRef.current = null; setDetected(null); return; }
      // Map video coords to on-screen px (video is object-cover).
      const cw = box.clientWidth, ch = box.clientHeight;
      const scale = Math.max(cw / v.videoWidth, ch / v.videoHeight);
      const dw = v.videoWidth * scale, dh = v.videoHeight * scale;
      const ox = (cw - dw) / 2, oy = (ch - dh) / 2;
      const view = { left: ox + next.x1 * dw, top: oy + next.y1 * dh, width: (next.x2 - next.x1) * dw, height: (next.y2 - next.y1) * dh };
      detectedRef.current = { box: next, view };
      setDetected(detectedRef.current);
    }, 250);
    return () => clearInterval(id);
  }, [editIdx, stream]);

  const stopCam = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  const toggleFlash = async () => {
    if (!stream || !torchSupported) return toast.error("Flash not supported on this camera");
    try {
      const track = stream.getVideoTracks()[0];
      await track.applyConstraints({ advanced: [{ torch: !flashOn }] });
      setFlashOn(!flashOn);
    } catch (e) { toast.error("Flash toggle failed"); }
  };

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0);
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    let brightness = 0;
    for (let i = 0; i < data.length; i += 400) brightness += (data[i] + data[i+1] + data[i+2]) / 3;
    brightness /= (data.length / 400);
    if (brightness < 40) toast.warning("Image looks dark — turn on flash or move to better light");
    const db = detectedRef.current?.box;
    const m = 0.015;
    const autoCrop = db ? {
      x1: Math.max(0, db.x1 - m) * 100, y1: Math.max(0, db.y1 - m) * 100,
      x2: Math.min(1, db.x2 + m) * 100, y2: Math.min(1, db.y2 + m) * 100,
    } : null;
    c.toBlob((blob) => {
      const newPage = {
        id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        blob,
        url: URL.createObjectURL(blob),
        rotation: 0,
        filter: "none",
        crop: autoCrop,
        autoCropped: !!autoCrop,
      };
      setPages((p) => [...p, newPage]);
      setEditIdx(pages.length); // edit the just-captured page
      setCropping(false);
    }, "image/jpeg", 0.92);
  };

  // Update the currently-edited page in place
  const updateEdit = (patch) => {
    setPages((prev) => prev.map((p, i) => (i === editIdx ? { ...p, ...patch } : p)));
  };
  const editingPage = editIdx >= 0 ? pages[editIdx] : null;
  const [cropPreview, setCropPreview] = useState(null);
  useEffect(() => {
    if (!editingPage?.crop || cropping) { setCropPreview(null); return undefined; }
    let live = true;
    renderPage({ ...editingPage, rotation: 0, filter: "none" }).then((r) => { if (live) setCropPreview(r.dataUrl); });
    return () => { live = false; };
  }, [editingPage?.url, editingPage?.crop, cropping]);

  const retakeCurrent = () => {
    // Discard the page currently being edited and return to camera view
    setPages((prev) => prev.filter((_, i) => i !== editIdx));
    setEditIdx(-1);
    setCropping(false);
  };

  const deletePage = (idx) => {
    setPages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Adjust editIdx if needed
      if (editIdx === idx) {
        // We were editing the one we just deleted — go back to camera
        setTimeout(() => setEditIdx(-1), 0);
      } else if (editIdx > idx) {
        setTimeout(() => setEditIdx((e) => e - 1), 0);
      }
      return next;
    });
  };

  const addAnotherPage = () => {
    setEditIdx(-1);
    setCropping(false);
  };

  const upload = async () => {
    if (pages.length === 0 || !bizId) return;
    setUploading(true);
    try {
      // Render every page's blob with its edits baked in
      const rendered = [];
      for (const p of pages) rendered.push(await renderPage(p));

      let finalFile;
      if (rendered.length === 1) {
        finalFile = new File([rendered[0].blob], `scan-${Date.now()}.jpg`, { type: "image/jpeg" });
      } else {
        // Build a single PDF containing all pages, one image per PDF page
        const pdf = new jsPDF({ unit: "pt", format: "a4" });
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        rendered.forEach((r, i) => {
          if (i > 0) pdf.addPage();
          // Fit image within A4 keeping aspect ratio, centered
          const scale = Math.min(pageW / r.width, pageH / r.height);
          const w = r.width * scale;
          const h = r.height * scale;
          const x = (pageW - w) / 2;
          const y = (pageH - h) / 2;
          pdf.addImage(r.dataUrl, "JPEG", x, y, w, h);
        });
        const pdfBlob = pdf.output("blob");
        finalFile = new File([pdfBlob], `scan-${Date.now()}.pdf`, { type: "application/pdf" });
      }

      const fd = new FormData();
      fd.append("business_id", bizId);
      fd.append("file", finalFile);
      const res = await documentsApi.upload(fd);
      toast.success(`${pages.length === 1 ? "Scan" : `${pages.length}-page scan`} uploaded — extracting…`);
      stopCam();
      nav(`/documents/${res.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  // Simple 2-point crop selection on preview (only when editing a page + crop mode on)
  const [dragStart, setDragStart] = useState(null);
  const onCropStart = (e) => {
    if (!cropping || !editingPage) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setDragStart({ x, y });
    updateEdit({ crop: { x1: x, y1: y, x2: x, y2: y } });
  };
  const onCropMove = (e) => {
    if (!cropping || !dragStart || !editingPage) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    updateEdit({
      crop: {
        x1: Math.min(dragStart.x, x), y1: Math.min(dragStart.y, y),
        x2: Math.max(dragStart.x, x), y2: Math.max(dragStart.y, y),
      },
    });
  };
  const onCropEnd = () => setDragStart(null);

  if (error) {
    return (
      <div className="p-8 max-w-lg">
        <div className="p-6 bg-status-duplicate/10 border border-status-duplicate/20 text-status-duplicate">{error}</div>
      </div>
    );
  }

  const inCameraMode = editIdx === -1;
  const currentFilter = editingPage ? (FILTERS.find((f) => f.key === editingPage.filter)?.filter || "none") : "none";

  return (
    <div className="fixed inset-0 bg-canvas-dark z-50 flex flex-col" data-testid="scan-camera">
      {/* Top bar */}
      <div className="flex items-center justify-between p-3 md:p-4 gap-2">
        <button onClick={() => { stopCam(); nav(-1); }} className="w-10 h-10 rounded-md bg-white/10 backdrop-blur-md flex items-center justify-center text-canvas hover:bg-white/20" data-testid="scan-close">
          <X className="w-5 h-5" />
        </button>
        {/* Business selector — always visible, works on iOS Safari (custom
            dropdown, portal-rendered, solid-contrast button). Master users can
            switch between businesses; non-master users see a read-only badge
            for their assigned business. */}
        <BusinessSelector
          businesses={businesses}
          value={bizId}
          onChange={setBizId}
          canSwitch={isMaster(user) || businesses.length > 1}
        />
        <div className="flex items-center gap-2">
          {pages.length > 0 && (
            <div className="px-3 py-1.5 rounded-md bg-white/10 text-canvas text-xs font-semibold border border-canvas/20" data-testid="scan-page-count">
              {pages.length} page{pages.length === 1 ? "" : "s"}
            </div>
          )}
          {inCameraMode && (
            <button onClick={toggleFlash} disabled={!torchSupported} data-testid="scan-flash" title={torchSupported ? "Toggle flash" : "Flash unsupported"} className={`w-10 h-10 rounded-md backdrop-blur-md flex items-center justify-center border border-canvas/20 ${flashOn ? "bg-gold text-espresso" : "bg-white/10 text-canvas hover:bg-white/20"} disabled:opacity-40`}>
              <Zap className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Main viewport. Camera mode has fixed inset; edit mode allows vertical
          scroll on tall receipts so mobile users can see the bottom of a long
          scan (iter72 bug — preview was cropped by overflow-hidden + max-h). */}
      <div ref={viewRef} className={`flex-1 min-h-0 relative ${inCameraMode ? "overflow-hidden" : (cropping ? "overflow-hidden" : "overflow-y-auto")}`}>
        {inCameraMode ? (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
            {detected && (
              <div
                data-testid="scan-detected-frame"
                className="absolute pointer-events-none border-2 border-gold rounded-sm bg-gold/10"
                style={{ ...detected.view, transition: "left 0.2s, top 0.2s, width 0.2s, height 0.2s", boxShadow: "0 0 0 9999px rgba(0,0,0,0.25), 0 0 14px #C68A4C" }}
              />
            )}
            <div className="absolute top-3 left-0 right-0 text-center pointer-events-none">
              <span className="px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md text-canvas text-xs md:text-sm font-semibold" data-testid="scan-hint">
                {detected ? "Document detected — tap capture" : pages.length === 0 ? "Point the camera at the document" : `Capture page ${pages.length + 1}`}
              </span>
            </div>
          </>
        ) : (
          <div className={`${cropping ? "absolute inset-0 flex items-center justify-center" : "min-h-full flex items-start justify-center"} p-2 md:p-4`}>
            <div
              className="relative max-w-full"
              onMouseDown={onCropStart} onMouseMove={onCropMove} onMouseUp={onCropEnd} onMouseLeave={onCropEnd}
              onTouchStart={(e) => {
                if (!cropping) return;
                e.preventDefault();
                const t = e.touches[0];
                onCropStart({ clientX: t.clientX, clientY: t.clientY, currentTarget: e.currentTarget });
              }}
              onTouchMove={(e) => {
                if (!cropping) return;
                e.preventDefault();
                const t = e.touches[0];
                onCropMove({ clientX: t.clientX, clientY: t.clientY, currentTarget: e.currentTarget });
              }}
              onTouchEnd={onCropEnd}
              style={{ cursor: cropping ? "crosshair" : "default", touchAction: cropping ? "none" : "pan-y", userSelect: "none", WebkitUserSelect: "none" }}
            >
              <img
                src={(!cropping && cropPreview) || editingPage?.url}
                alt="capture"
                data-testid="scan-preview"
                draggable={false}
                className={`w-auto pointer-events-none ${cropping ? "max-h-[calc(100vh-260px)]" : "max-w-full"}`}
                style={{
                  transform: `rotate(${editingPage?.rotation || 0}deg)`,
                  filter: currentFilter,
                  transition: "transform 0.2s",
                }}
              />
              {cropping && editingPage?.crop && (
                <div
                  className="absolute border-2 border-gold bg-gold/10 pointer-events-none"
                  style={{
                    left: `${editingPage.crop.x1}%`, top: `${editingPage.crop.y1}%`,
                    width: `${editingPage.crop.x2 - editingPage.crop.x1}%`, height: `${editingPage.crop.y2 - editingPage.crop.y1}%`,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Page thumbnails row (shown when there's at least one page) */}
      {pages.length > 0 && (
        <div className="bg-canvas-dark/95 backdrop-blur-md border-t border-white/10 px-3 py-2 overflow-x-auto" data-testid="page-thumbs">
          <div className="flex items-center gap-2 min-w-max">
            {pages.map((p, i) => (
              <div key={p.id} className="relative shrink-0">
                <button
                  onClick={() => { setEditIdx(i); setCropping(false); }}
                  data-testid={`page-thumb-${i}`}
                  className={`w-16 h-20 rounded-md overflow-hidden border-2 ${editIdx === i ? "border-gold" : "border-white/20 hover:border-white/40"}`}
                >
                  <img src={p.url} alt={`page ${i + 1}`} className="w-full h-full object-cover" style={{ transform: `rotate(${p.rotation}deg)` }} />
                </button>
                <button
                  onClick={() => deletePage(i)}
                  data-testid={`page-thumb-delete-${i}`}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-status-duplicate text-white flex items-center justify-center text-xs hover:scale-110"
                  title={`Delete page ${i + 1}`}
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-canvas text-[10px] text-center py-0.5">#{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom controls */}
      {inCameraMode ? (
        <div className="p-6 flex justify-center items-center gap-4" data-testid="camera-controls">
          <div className="flex-1 flex justify-end">
            {pages.length > 0 && (
              <button
                onClick={() => setEditIdx(pages.length - 1)}
                data-testid="review-pages-btn"
                className="px-4 py-2.5 rounded-md bg-white/10 text-canvas font-semibold hover:bg-white/20 inline-flex items-center gap-2 text-sm"
              >
                <FileText className="w-4 h-4" /> Review &amp; upload
              </button>
            )}
          </div>
          <button data-testid="scan-capture" onClick={capture} className="w-20 h-20 rounded-full bg-white border-4 border-gold hover:scale-105 transition-transform flex items-center justify-center shrink-0">
            <div className="w-14 h-14 rounded-full bg-gold" />
          </button>
          <div className="flex-1" />
        </div>
      ) : (
        <div className="bg-canvas-dark/95 backdrop-blur-md border-t border-white/10">
          {/* Editing toolbar */}
          <div className="px-4 py-3 border-b border-white/10 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              <button onClick={() => updateEdit({ rotation: (editingPage?.rotation || 0) - 90 })} data-testid="rotate-left" className="px-3 py-2 rounded-md bg-white/10 text-canvas hover:bg-white/20 inline-flex items-center gap-1.5 text-xs font-semibold"><RotateCcw className="w-3.5 h-3.5" /> Left</button>
              <button onClick={() => updateEdit({ rotation: (editingPage?.rotation || 0) + 90 })} data-testid="rotate-right" className="px-3 py-2 rounded-md bg-white/10 text-canvas hover:bg-white/20 inline-flex items-center gap-1.5 text-xs font-semibold"><RotateCw className="w-3.5 h-3.5" /> Right</button>
              <div className="w-px h-6 bg-white/20" />
              {FILTERS.map((f) => (
                <button key={f.key} onClick={() => updateEdit({ filter: f.key })} data-testid={`filter-${f.key}`} className={`px-3 py-2 rounded-md text-xs font-semibold ${editingPage?.filter === f.key ? "bg-gold text-espresso" : "bg-white/10 text-canvas hover:bg-white/20"}`}>{f.label}</button>
              ))}
              <div className="w-px h-6 bg-white/20" />
              <button onClick={() => setCropping(!cropping)} data-testid="toggle-crop" className={`px-3 py-2 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${cropping ? "bg-gold text-espresso" : "bg-white/10 text-canvas hover:bg-white/20"}`}>
                <Crop className="w-3.5 h-3.5" /> {cropping ? "Crop mode on" : "Crop"}
              </button>
              {editingPage?.crop && <button onClick={() => updateEdit({ crop: null, autoCropped: false })} data-testid="clear-crop" className="px-2 py-2 text-xs text-canvas/70 hover:text-canvas">{editingPage.autoCropped ? "Undo auto-crop" : "Clear crop"}</button>}
            </div>
          </div>
          {/* Action buttons */}
          <div className="p-4 md:p-6 flex items-center justify-center gap-3 flex-wrap">
            <button onClick={retakeCurrent} className="px-4 py-2.5 rounded-md bg-white/10 text-canvas font-semibold hover:bg-white/20 inline-flex items-center gap-2 text-sm" data-testid="scan-retake">
              <RefreshCcw className="w-4 h-4" /> Retake page
            </button>
            <button onClick={addAnotherPage} className="px-4 py-2.5 rounded-md bg-white/10 text-canvas font-semibold hover:bg-white/20 inline-flex items-center gap-2 text-sm" data-testid="scan-add-page">
              <Plus className="w-4 h-4" /> Add another page
            </button>
            <button onClick={upload} disabled={uploading || !bizId || pages.length === 0} className="px-5 py-2.5 rounded-md bg-gold text-espresso font-semibold hover:bg-gold-light inline-flex items-center gap-2 disabled:opacity-50 text-sm" data-testid="scan-upload">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {pages.length > 1 ? `Upload ${pages.length}-page PDF` : "Confirm & upload"}
            </button>
          </div>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

// Robust business picker — a visible styled shell with an invisible native
// <select> overlaid on top. The native select is what iOS Safari (and every
// other browser) actually taps — it opens the OS-native picker wheel, which
// is 100% reliable on iOS where custom onClick + portal patterns fail in
// fullscreen fixed-inset containers.
function BusinessSelector({ businesses, value, onChange, canSwitch }) {
  const current = businesses.find((b) => b.id === value);
  const label = current?.name || "Choose business";

  // Read-only badge for single-business users
  if (!canSwitch) {
    return (
      <div className="px-3 py-2 rounded-md bg-espresso text-canvas text-sm font-semibold inline-flex items-center gap-2 max-w-[60vw] truncate" data-testid="scan-biz-badge">
        <Building2 className="w-4 h-4 shrink-0 text-gold" />
        <span className="truncate">{label}</span>
      </div>
    );
  }

  return (
    <div className="relative inline-block" data-testid="scan-biz-btn">
      {/* Visible display shell — pointer-events off so the native select
          underneath captures every tap. */}
      <div
        className="px-3 py-2 rounded-md bg-espresso text-canvas text-sm font-semibold inline-flex items-center gap-2 max-w-[60vw] border border-gold/40 shadow-soft pointer-events-none"
      >
        <Building2 className="w-4 h-4 shrink-0 text-gold" />
        <span className="truncate max-w-[40vw]">{label}</span>
        <ChevronDown className="w-4 h-4 shrink-0 opacity-70" />
      </div>
      {/* Invisible native <select> stretched to cover the shell — this is
          what actually receives the tap on iOS Safari. Removing all custom
          appearance so iOS uses its native picker wheel. */}
      <select
        data-testid="scan-biz-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        style={{ WebkitAppearance: "none", MozAppearance: "none", appearance: "none", fontSize: "16px" /* prevents iOS zoom on focus */ }}
        aria-label="Scan into business"
      >
        {businesses.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </select>
    </div>
  );
}
